import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const TemperatureConverter = () => {
  const [inputValue, setInputValue] = useState('0');
  const [fromUnit, setFromUnit] = useState('celsius');
  const [toUnit, setToUnit] = useState('fahrenheit');
  const [result, setResult] = useState('');

  const temperatureUnits = [
    { key: 'celsius', name: 'Celsius (°C)', symbol: '°C' },
    { key: 'fahrenheit', name: 'Fahrenheit (°F)', symbol: '°F' },
    { key: 'kelvin', name: 'Kelvin (K)', symbol: 'K' }
  ];

  const convertTemperature = (value, from, to) => {
    if (from === to) return value;

    let celsius;
    
    // Convert to Celsius first
    switch (from) {
      case 'celsius':
        celsius = value;
        break;
      case 'fahrenheit':
        celsius = (value - 32) * 5/9;
        break;
      case 'kelvin':
        celsius = value - 273.15;
        break;
      default:
        return 0;
    }

    // Convert from Celsius to target
    switch (to) {
      case 'celsius':
        return celsius;
      case 'fahrenheit':
        return celsius * 9/5 + 32;
      case 'kelvin':
        return celsius + 273.15;
      default:
        return 0;
    }
  };

  const calculateConversion = () => {
    const value = parseFloat(inputValue);
    if (isNaN(value)) {
      setResult('');
      return;
    }

    const converted = convertTemperature(value, fromUnit, toUnit);
    setResult(converted.toFixed(2));
  };

  useEffect(() => {
    calculateConversion();
  }, [inputValue, fromUnit, toUnit]);

  const getAllTemperatures = () => {
    const value = parseFloat(inputValue);
    if (isNaN(value)) return [];

    return temperatureUnits.map(unit => ({
      name: unit.name,
      symbol: unit.symbol,
      value: convertTemperature(value, fromUnit, unit.key).toFixed(2)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="space-y-4">
        <div>
          <label className="block text-white font-medium mb-2">
            Temperatur
          </label>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Temperatur eingeben"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white font-medium mb-2">
              Von
            </label>
            <select
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              {temperatureUnits.map(unit => (
                <option key={unit.key} value={unit.key} className="bg-gray-800">
                  {unit.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white font-medium mb-2">
              Nach
            </label>
            <select
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              {temperatureUnits.map(unit => (
                <option key={unit.key} value={unit.key} className="bg-gray-800">
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10 text-center"
        >
          <h3 className="text-xl font-bold text-white mb-4">
            Ergebnis
          </h3>
          <div className="text-3xl font-bold text-purple-300 mb-2">
            {result}
          </div>
          <div className="text-white/70">
            {temperatureUnits.find(u => u.key === toUnit)?.symbol}
          </div>
        </motion.div>
      )}

      {/* All Temperatures */}
      {inputValue && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10"
        >
          <h3 className="text-xl font-bold text-white mb-4 text-center">
            {inputValue} {temperatureUnits.find(u => u.key === fromUnit)?.symbol} entspricht:
          </h3>
          <div className="space-y-3">
            {getAllTemperatures().map((temp, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-white/10 last:border-b-0">
                <span className="text-white/70">{temp.name}</span>
                <span className="text-white font-medium text-lg">
                  {temp.value} {temp.symbol}
                </span>
              </div>
            ))}
          </div>

          {/* Temperature Reference Points */}
          <div className="mt-6 pt-4 border-t border-white/10">
            <h4 className="text-lg font-semibold text-white mb-3">Referenzpunkte</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/70">Absolute Null:</span>
                  <span className="text-white">-273.15°C / -459.67°F / 0K</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Gefrierpunkt Wasser:</span>
                  <span className="text-white">0°C / 32°F / 273.15K</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/70">Körpertemperatur:</span>
                  <span className="text-white">37°C / 98.6°F / 310.15K</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Siedepunkt Wasser:</span>
                  <span className="text-white">100°C / 212°F / 373.15K</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TemperatureConverter;